import { useEffect, type RefObject } from "react";

function useEscapeClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [open, onClose]);
}

function useFocusTrap(open: boolean, dialogRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open || !dialogRef.current) {
      return;
    }
    const dialog = dialogRef.current;
    const focusableList = [
      ...dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const first = focusableList[0];
    const last = focusableList.at(-1);
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Tab") {
        return;
      }
      const active: HTMLElement | undefined =
        document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [open, dialogRef]);
}

export function useDialogBehavior(
  open: boolean,
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEscapeClose(open, onClose);
  useFocusTrap(open, dialogRef);
}
